import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { addToast } from "@icg360/design-system";

import { useDecodeData, usePoliciesList } from "data/ss-data-hooks";
import { trackEvent, urlSearchParamsObject } from "utils";

export const useAddPolicyDataFromEmailLink = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const parsedQS = urlSearchParamsObject(location.search);
  const [addPolicyData, setAddPolicyData] = useState<{
    policyNumber: string;
    zipCode: string;
  }>();

  const { data: decodedUserData, isLoading: isDecoding } = useDecodeData(
    parsedQS.data,
  );

  useEffect(() => {
    if (isDecoding) {
      return;
    }
    if (parsedQS.modal) {
      if (parsedQS.data) {
        setAddPolicyData({
          policyNumber: decodedUserData?.policyId ?? "",
          zipCode: decodedUserData?.zipcode ?? "",
        });
      }
      navigate(location.pathname);
    }
  }, [
    isDecoding,
    decodedUserData,
    location.pathname,
    navigate,
    parsedQS.data,
    parsedQS.modal,
  ]);

  return [addPolicyData];
};

export const useAddPolicy = (addPolicyData) => {
  const { mutate: refetchPolicies } = usePoliciesList();
  const [showAddPolicyModal, setShowAddPolicyModal] = useState(false);

  useEffect(() => {
    if (addPolicyData) {
      setShowAddPolicyModal(true);
    }
  }, [addPolicyData]);

  useEffect(() => {
    if (showAddPolicyModal) {
      if (addPolicyData) {
        trackEvent(
          "Add Policy Modal Displayed (email enrollment)",
          addPolicyData,
        );
      } else {
        trackEvent("Add Policy Modal Displayed");
      }
    }
  }, [showAddPolicyModal, addPolicyData]);

  const openAddPolicyModal = () => {
    setShowAddPolicyModal(true);
    trackEvent("Add Policy Started");
  };

  const closeAddPolicyModal = () => {
    trackEvent("Add Policy Modal Closed");
    setShowAddPolicyModal(false);
  };

  const handleAddPolicySucceeded = async ({ newPolicyId, zipCode, origin }) => {
    refetchPolicies();

    setShowAddPolicyModal(false);

    addToast("Policy successfully added.", {
      icon: true,
      actionName: "View policy",
      action: () => {
        window.location.href = `/my/overview?policyNumber=${newPolicyId}`;
      },
    });

    trackEvent("Policy Added", {
      policyNumber: newPolicyId,
      zipCode: zipCode,
      addPolicyOrigin: origin,
    });
  };

  return {
    showAddPolicyModal,
    addPolicyData,
    openAddPolicyModal,
    closeAddPolicyModal,
    handleAddPolicySucceeded,
  };
};
